/**
 * CRS Radar Chart - 7 dimension scores plotted on a radar (SVG, no deps).
 */
import React from 'react';
import { CRSDimension, CRS_DIMENSIONS, DimensionScores } from '../types/capitalReadiness';

interface Props {
  scores: DimensionScores;
  size?: number;
  className?: string;
}

const DIMENSION_LABELS: Record<CRSDimension, string> = {
  financial_preparedness: 'Financial',
  market_validation: 'Market',
  team_strength: 'Team',
  legal_compliance: 'Legal',
  pitch_quality: 'Pitch',
  traction_metrics: 'Traction',
  investor_readiness: 'Investor',
};

const CRSRadarChart: React.FC<Props> = ({ scores, size = 320, className = '' }) => {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.36;
  const n = CRS_DIMENSIONS.length;

  // Compute angle for each axis (starting at top, going clockwise)
  const pointFor = (i: number, r: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r };
  };

  // Grid rings at 20/40/60/80/100
  const rings = [0.2, 0.4, 0.6, 0.8, 1.0];

  // Value polygon
  const valuePoints = CRS_DIMENSIONS.map((d, i) => {
    const v = Math.max(0, Math.min(100, Number(scores[d] || 0))) / 100;
    return pointFor(i, radius * v);
  });
  const valuePath = valuePoints.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      height="100%"
      className={className}
      style={{ maxWidth: size }}
    >
      {/* Grid rings */}
      {rings.map((r, idx) => {
        const pts = CRS_DIMENSIONS.map((_, i) => {
          const p = pointFor(i, radius * r);
          return `${p.x},${p.y}`;
        }).join(' ');
        return (
          <polygon
            key={idx}
            points={pts}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={1}
          />
        );
      })}

      {/* Axis lines */}
      {CRS_DIMENSIONS.map((_, i) => {
        const p = pointFor(i, radius);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
            stroke="#E5E7EB"
            strokeWidth={1}
          />
        );
      })}

      {/* Value polygon */}
      <polygon
        points={valuePath}
        fill="rgba(59, 130, 246, 0.25)"
        stroke="#2563EB"
        strokeWidth={2}
      />

      {/* Value points */}
      {valuePoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3.5} fill="#2563EB" />
      ))}

      {/* Labels */}
      {CRS_DIMENSIONS.map((d, i) => {
        const p = pointFor(i, radius + 20);
        const score = Math.round(Number(scores[d] || 0));
        return (
          <g key={d}>
            <text
              x={p.x}
              y={p.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-700"
              style={{ fontSize: 11, fontWeight: 500 }}
            >
              {DIMENSION_LABELS[d]}
            </text>
            <text
              x={p.x}
              y={p.y + 13}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-500"
              style={{ fontSize: 10 }}
            >
              {score}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default CRSRadarChart;
